import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, map, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LocaleService } from '@core/i18n/locale.service';
import {
  PerguntaParametrizadaResponse,
  PerguntaRespostaResponse,
} from './models/pergunta.models';

@Injectable({ providedIn: 'root' })
export class PerguntaService {
  private readonly http = inject(HttpClient);
  private readonly localeService = inject(LocaleService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly url = `${environment.apiUrl}/perguntas`;

  /** Cache por locale — a API já devolve os textos traduzidos. */
  private readonly cache = new Map<
    string,
    Observable<PerguntaParametrizadaResponse[]>
  >();

  /**
   * Lista de perguntas parametrizadas (fonte única da nuvem e do detalhe).
   * O resultado é compartilhado/cacheado por locale.
   */
  getPerguntas(): Observable<PerguntaParametrizadaResponse[]> {
    // No SSR/prerender não há backend acessível via `/api`; os dados são
    // carregados no browser após a hidratação.
    if (!this.isBrowser) return EMPTY;
    const locale = this.localeService.currentLocale;
    let stream = this.cache.get(locale);
    if (!stream) {
      stream = this.http
        .get<PerguntaParametrizadaResponse[]>(this.url)
        .pipe(shareReplay(1));
      this.cache.set(locale, stream);
    }
    return stream;
  }

  /**
   * Encontra uma variante da listagem pelo id da pergunta na API
   * (`pergunta.id`, o mesmo usado no path e em `/perguntas/{id}/resposta`).
   * Quando há várias variantes do mesmo id, prefere a que casa com os
   * query params (ex.: `coluna=municipio`).
   */
  getPerguntaParametrizadaByPerguntaId(
    perguntaId: number,
    queryParams: Record<string, string> = {},
  ): Observable<PerguntaParametrizadaResponse | undefined> {
    return this.getPerguntas().pipe(
      map((perguntas) => {
        const candidates = perguntas.filter(
          (p) => p.pergunta?.id === perguntaId,
        );
        if (candidates.length === 0) return undefined;
        if (candidates.length === 1) return candidates[0];

        let best = candidates[0];
        let bestScore = -1;
        for (const candidate of candidates) {
          const cardParams =
            candidate.parametros && typeof candidate.parametros === 'object'
              ? (candidate.parametros as Record<string, string>)
              : {};
          const score = Object.entries(cardParams).filter(
            ([key, value]) =>
              key !== 'typeMir' && queryParams[key] === String(value),
          ).length;
          if (score > bestScore) {
            bestScore = score;
            best = candidate;
          }
        }
        return best;
      }),
    );
  }

  /**
   * Consulta a resposta (dados do gráfico) de uma pergunta.
   * `params` é um mapa `{ nomeParametro: valor }`.
   */
  getResposta(
    perguntaId: number,
    params: Record<string, string>,
  ): Observable<PerguntaRespostaResponse> {
    if (!this.isBrowser) return EMPTY;
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, value);
    }
    return this.http.get<PerguntaRespostaResponse>(
      `${this.url}/${perguntaId}/resposta`,
      { params: httpParams },
    );
  }

  /**
   * Autocomplete de municípios (param `uf_autocomplete`).
   * Retorna nomes no formato "MUNICÍPIO (UF)".
   */
  getMunicipioAutocomplete(query: string): Observable<string[]> {
    if (!this.isBrowser) return EMPTY;
    const normalized = query
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
      .trim();
    if (!normalized) return EMPTY;
    return this.http.get<string[]>(
      `${environment.apiUrl}/siconv/municipio/${encodeURIComponent(normalized)}`,
    );
  }
}
